import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Heart,
  Users,
  Share2,
  Sparkles,
  Trophy,
  MessageCircle,
  CheckCircle2,
  X,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";

const SHARE_URL = "https://saral-ai.com/";
const SAHAJ_PATH_URL = "https://chat.whatsapp.com/IVVtlhBFVVOEIiS5Ymppte";

/** Level Blaze thresholds (name → minimum helped_count). */
const LEVELS = [
  { key: "student",   min: 0,  max: 0,  gradient: "from-slate-400 to-slate-600",     emoji: "🌱" },
  { key: "helper",    min: 1,  max: 4,  gradient: "from-teal-400 to-emerald-500",    emoji: "🌿" },
  { key: "supporter", min: 5,  max: 9,  gradient: "from-sky-400 to-indigo-500",      emoji: "💫" },
  { key: "mentor",    min: 10, max: 24, gradient: "from-fuchsia-400 to-pink-500",    emoji: "🌟" },
  { key: "champion",  min: 25, max: Infinity, gradient: "from-amber-400 to-rose-500", emoji: "🏆" },
];

function levelFor(count) {
  const c = Number(count) || 0;
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (c >= LEVELS[i].min) return { ...LEVELS[i], index: i };
  }
  return { ...LEVELS[0], index: 0 };
}

/** Compose the WhatsApp share URL. `wa.me/?text=…` opens the native
 *  WhatsApp app on mobile and web.whatsapp.com in desktop browsers. No
 *  API key or subscription is required. */
function buildWhatsAppShareUrl(message) {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export default function HelpAFriend() {
  const navigate = useNavigate();
  const { user, lang, updateHelpedCount } = useAuth();
  const [busy, setBusy] = useState(false);
  // After the user taps "Share on WhatsApp" we open WhatsApp in a NEW tab
  // (so our page stays alive) and surface a confirmation strip. The helped
  // count only increments when the user explicitly taps "Yes, I shared it".
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const helped = user?.helped_count ?? 0;
  const current = levelFor(helped);
  const next = LEVELS[current.index + 1];   // may be undefined at Champion
  const remaining = next && next.min > helped ? next.min - helped : 0;

  // Progress within the current level (0..1). At Champion → always 1.
  const progressPct = (() => {
    if (!next) return 100;
    const span = Math.max(1, next.min - current.min);
    const done = Math.max(0, helped - current.min);
    return Math.max(0, Math.min(100, Math.round((done / span) * 100)));
  })();

  // On mount: hydrate helped_count from /me if it's not on the user object.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/me");
        if (alive && typeof r?.data?.helped_count === "number") {
          updateHelpedCount(r.data.helped_count);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onShare = () => {
    if (busy) return;
    // Build the exact WhatsApp message the user asked for.
    const msg =
`📚 ${t(lang, "help_a_friend_msg_greeting")} ❤️
${t(lang, "help_a_friend_msg_body")}
${SHARE_URL}
${t(lang, "help_a_friend_msg_tagline")} 🌱`;

    // Open WhatsApp in a NEW tab so this page stays alive to receive the
    // user's confirmation after they finish sharing. Native mobile browsers
    // hand off wa.me to the WhatsApp app either way.
    try {
      window.open(buildWhatsAppShareUrl(msg), "_blank", "noopener,noreferrer");
    } catch (_) { /* ignore — user can still tap "Yes, I shared" if the popup was blocked */ }

    setPendingConfirm(true);
  };

  const onConfirmShared = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/referrals/increment");
      const n = r?.data?.helped_count;
      if (typeof n === "number") {
        updateHelpedCount(n);
        const nextLevelName = next ? t(lang, `level_${next.key}`) : null;
        toast.success(
          nextLevelName && n < next.min
            ? t(lang, "helped_toast_progress")
                .replace("{n}", String(n))
                .replace("{next}", nextLevelName)
                .replace("{remain}", String(next.min - n))
            : t(lang, "helped_toast_simple").replace("{n}", String(n))
        );
      }
      setPendingConfirm(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Something went wrong"
      );
    } finally {
      setBusy(false);
    }
  };

  const onDismissConfirm = () => {
    if (busy) return;
    setPendingConfirm(false);
  };

  const onJoinSahaj = () => {
    // Voluntary — we merely open the invite. The user chooses to join.
    window.location.href = SAHAJ_PATH_URL;
  };

  return (
    <div className="page-shell" data-testid="help-a-friend-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="haf-back-btn"
          onClick={() => navigate("/")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={20} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "help_a_friend")}
        </h2>
        <div className="w-12" />
      </div>

      {/* Hero — heart */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mt-6 text-center"
      >
        <motion.div
          className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center border-4 border-white shadow-[0_10px_0_rgba(0,0,0,0.10)]"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <Heart size={38} strokeWidth={2.75} className="text-white" fill="currentColor" />
        </motion.div>
        <p className="mt-4 text-cyan-50 font-heading text-lg font-bold drop-shadow">
          {t(lang, "help_a_friend_intro")}
        </p>
      </motion.div>

      {/* Level Blaze card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        data-testid="level-blaze-card"
        className={`mt-6 rounded-3xl p-5 border-4 border-white text-white bg-gradient-to-br ${current.gradient} shadow-[0_10px_0_rgba(0,0,0,0.12)]`}
      >
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center text-3xl">
            {current.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90 flex items-center gap-1">
              <Trophy size={12} strokeWidth={3} /> {t(lang, "level_blaze")}
            </div>
            <div className="font-heading text-2xl font-bold leading-tight" data-testid="level-name">
              {t(lang, `level_${current.key}`)}
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-4xl font-bold tabular-nums" data-testid="helped-count">
              {helped}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-90">
              {t(lang, "helped_label")}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="h-2.5 rounded-full bg-white/25 overflow-hidden">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <p className="mt-3 text-sm font-bold text-white/95 leading-snug" data-testid="progress-text">
            {next
              ? t(lang, "progress_line")
                  .replace("{n}", String(helped))
                  .replace("{remain}", String(remaining))
                  .replace("{next}", t(lang, `level_${next.key}`))
              : t(lang, "progress_line_top").replace("{n}", String(helped))}
          </p>
        </div>
      </motion.div>

      {/* Share button */}
      <motion.button
        data-testid="whatsapp-share-btn"
        onClick={onShare}
        disabled={busy}
        whileTap={{ scale: 0.97 }}
        className="mt-5 w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-heading text-lg font-bold uppercase tracking-wide py-4 rounded-2xl btn-squish inline-flex items-center justify-center gap-3 border-2 border-white shadow-[0_6px_0_rgba(0,0,0,0.15)]"
      >
        <Share2 size={22} strokeWidth={2.75} />
        {t(lang, "share_on_whatsapp")}
      </motion.button>
      <p className="mt-2 text-center text-xs text-cyan-100/85 font-semibold drop-shadow">
        <Sparkles size={12} strokeWidth={3} className="inline -mt-0.5 mr-1" />
        {t(lang, "share_helper_line")}
      </p>

      {/* Confirmation strip — the count only bumps when the user confirms
          they actually completed the WhatsApp share. */}
      <AnimatePresence>
        {pendingConfirm && (
          <motion.div
            key="share-confirm"
            data-testid="share-confirm-strip"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mt-3 bg-white rounded-2xl p-4 border-4 border-emerald-300 shadow-[0_4px_0_rgba(0,0,0,0.08)]"
          >
            <p className="text-sm font-heading font-bold text-slate-800 text-center">
              {t(lang, "share_confirm_prompt")}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                data-testid="share-confirm-yes"
                onClick={onConfirmShared}
                disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-heading font-bold uppercase text-sm py-2.5 rounded-xl btn-squish inline-flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={16} strokeWidth={2.75} />
                {t(lang, "share_confirm_yes")}
              </button>
              <button
                data-testid="share-confirm-no"
                onClick={onDismissConfirm}
                disabled={busy}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-heading font-bold uppercase text-sm py-2.5 rounded-xl btn-squish inline-flex items-center justify-center gap-2"
              >
                <X size={16} strokeWidth={2.75} />
                {t(lang, "share_confirm_no")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sahaj Path WhatsApp group */}
      <motion.button
        data-testid="sahaj-path-btn"
        onClick={onJoinSahaj}
        whileTap={{ scale: 0.97 }}
        className="mt-6 w-full bg-white text-slate-800 font-heading text-lg font-bold uppercase tracking-wide py-4 rounded-2xl btn-squish inline-flex items-center justify-center gap-3 border-4 border-slate-900/10 shadow-[0_6px_0_rgba(0,0,0,0.08)]"
      >
        <MessageCircle size={22} strokeWidth={2.75} className="text-emerald-500" />
        <span className="text-slate-800">{t(lang, "join_sahaj_path")}</span>
      </motion.button>
      <p className="mt-2 text-center text-xs text-cyan-100/85 font-semibold drop-shadow inline-flex items-center gap-1 w-full justify-center">
        <Users size={12} strokeWidth={3} />
        {t(lang, "sahaj_path_hint")}
      </p>

      <div className="h-6" />
    </div>
  );
}
